/**
 * Клиент админского API.
 *
 * Токен здесь ОДИН и он админский. Игрового контура эта сборка не знает
 * вовсе: нет ни маршрута `/auth/login`, ни хранилища для его токена.
 * Так правило №4 («игровым токеном нельзя открыть админку») держится не
 * только гвардом на сервере, но и тем, что в админке нечему перепутаться.
 *
 * Токен живёт в `sessionStorage`, а не в `localStorage`: он даёт право
 * править экономику прода, и закрытая вкладка обязана это право забирать.
 * TTL у него восемь часов, но вкладку закрывают чаще.
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'

const TOKEN_KEY = 'lust.admin.token'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

let token: string | null = sessionStorage.getItem(TOKEN_KEY)

/** Слушатели «сессия кончилась». Их зовёт любой 401 с сервера. */
const expiryListeners = new Set<() => void>()

export function onSessionExpired(listener: () => void): () => void {
  expiryListeners.add(listener)
  return () => expiryListeners.delete(listener)
}

export function getToken(): string | null {
  return token
}

export function setToken(next: string | null): void {
  token = next
  if (next === null) sessionStorage.removeItem(TOKEN_KEY)
  else sessionStorage.setItem(TOKEN_KEY, next)
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  /**
   * `FormData` — для загрузки файла (фото героини): отправляется как есть,
   * без `content-type` в заголовках. Браузер сам подставит
   * `multipart/form-data` с правильным boundary — заголовок, выставленный
   * руками, боится не совпасть с тем, что fetch реально положит в тело.
   */
  body?: unknown | FormData
  query?: Record<string, string | number | undefined>
  /** Не сбрасывать сессию на 401. Нужно самому входу: там 401 — это
   *  «неверный пароль», а не «протухший токен». */
  keepSessionOn401?: boolean
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_URL}${path}`)
  for (const [key, value] of Object.entries(opts.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }

  const isFormData = opts.body instanceof FormData

  const headers: Record<string, string> = {}
  if (opts.body !== undefined && !isFormData) headers['content-type'] = 'application/json'
  if (token !== null) headers.authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : isFormData ? (opts.body as FormData) : JSON.stringify(opts.body),
    })
  } catch (err) {
    // Сеть, а не сервер: отдельное сообщение, потому что чинится оно иначе.
    throw new ApiError(0, 'network', `API is unreachable (${API_URL}): ${(err as Error).message}`)
  }

  if (response.status === 204) return undefined as T

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const body = (payload ?? {}) as { error?: string; message?: string; details?: unknown }

    if (response.status === 401 && opts.keepSessionOn401 !== true) {
      setToken(null)
      for (const listener of expiryListeners) listener()
    }

    throw new ApiError(
      response.status,
      body.error ?? 'unknown',
      body.message ?? `HTTP ${response.status}`,
      body.details,
    )
  }

  return payload as T
}
